import {
  moduleDeLaNavigation,
  moduleDeLaRoute,
  type Action,
  type Module,
  type ModulePilotable,
} from '@artosera/shared';
import { ApiError } from '../api/errors.js';
import type { GalerieSession } from '../api/types.js';
import { modulesDeLaSession, modulesLectureSeuleDeLaSession } from './sujet.js';

/**
 * Modules consultables en LECTURE SEULE (« tout voir, rien créer »).
 *
 * Le contrat de l'API : `modulesLectureSeule` dans la session, leurs GET
 * répondent, toute écriture est refusée par `403 module.indisponible` (raison
 * `module_lecture_seule`). La régie n'attend pas ce refus pour s'effacer :
 *
 *  - la navigation montre le module avec un cadenas ;
 *  - ses pages s'ouvrent sous une « galerie de page » (`galerieDeLaPage`) qui
 *    ne porte plus aucune action d'écriture — les boutons, formulaires et
 *    ouvertures par URL (`?nouveau=1`) gardés par `galerieALaction` se
 *    comportent donc exactement comme pour un rôle sans droit d'écriture ;
 *  - là où l'écran garde la place d'un bouton, `BoutonVerrouille` la montre
 *    avec son cadenas.
 */

/**
 * Le module PILOTABLE qui possède chacune de ces actions d'écriture : elles ne
 * servent à aucun autre module, ni au cœur. Les écritures partagées
 * (`documents:write`, `exhibitions:write`, `settings:write`, `ia:settings`…)
 * n'y figurent pas : elles tombent par la page du module (`galerieDeLaPage`)
 * ou par le module que la section teste (`galerieEcritModule`).
 *
 * Tiré des gardes de l'API (action, dossier de routes, module du registre
 * partagé) ; `lectureSeule.test.ts` garde les actions inconnues dehors.
 */
const MODULE_DE_L_ACTION: Readonly<Partial<Record<Action, ModulePilotable>>> = {
  'invoices:write': 'facturation',
  'invoices:issue': 'facturation',
  'invoices:credit': 'facturation',
  'payments:write': 'facturation',
  'viewing-rooms:write': 'viewing-rooms',
  'offers:write': 'offres-relances',
  'offers:send': 'offres-relances',
  'contracts:write': 'modeles-contrats',
  'contracts:templates': 'modeles-contrats',
  'movements:write': 'mouvements',
  'editions:write': 'editions-provenance',
  'artist-contracts:write': 'contrats-artistes',
  'agenda:write': 'agenda-presse',
  'press:write': 'agenda-presse',
  'newsletter:write': 'newsletter-reseaux',
  'newsletter:send': 'newsletter-reseaux',
  'diffusion:write': 'artsy-artnet',
  'diffusion:manage': 'artsy-artnet',
  'site:write': 'site',
  'site:publish': 'site',
  'posts:write': 'site',
  'pages:write': 'site',
  'orders:refund': 'boutique',
  'shop:write': 'boutique',
  'pipeline:write': 'pipeline-ventes',
  'profils:write': 'profils-collectionneurs',
  'collector-space:write': 'espace-collectionneur',
  'events:write': 'evenements',
  'events:checkin': 'evenements',
  'presentation:write': 'rendez-vous-client',
  'negociation:approuver': 'rendez-vous-client',
  'creneaux:write': 'prise-rendez-vous',
  'creneaux:configurer': 'prise-rendez-vous',
  'inbox:write': 'boite-reception',
  'inbox:settings': 'boite-reception',
  'studio:write': 'studio-contenu',
  'inventaire:write': 'reserve-inventaire',
  'inventaire:manage': 'reserve-inventaire',
  'insurance:write': 'valeurs-assurance',
  'insurance:settings': 'valeurs-assurance',
  'artist-accounts:write': 'comptes-artistes',
  'artist-accounts:validate': 'comptes-artistes',
  'online-payments:write': 'paiement-en-ligne',
  'online-payments:manage': 'paiement-en-ligne',
  'treasury:write': 'tresorerie',
  'treasury:settings': 'tresorerie',
  'registre-police:inscrire': 'registre-police',
  'registre-police:rectifier': 'registre-police',
  'locations:write': 'location-oeuvres',
  'locations:facturer': 'location-oeuvres',
};

/** Suffixes d'action qui ne modifient rien : liste de prix, export, aperçu, journal. */
const SUFFIXES_DE_LECTURE = new Set(['price-list', 'export', 'export-all', 'audit', 'preview']);

/**
 * Cette action ne modifie rien ? `x:read…` et les consultations nommées
 * (liste de prix, export, aperçu, journal) restent permises en lecture seule.
 */
export function estActionDeLecture(action: Action): boolean {
  const suffixe = action.slice(action.indexOf(':') + 1);
  return suffixe.startsWith('read') || SUFFIXES_DE_LECTURE.has(suffixe);
}

/** Le module pilotable propre à cette action d'écriture, s'il y en a un. */
export function moduleDeLActionEcriture(action: Action): ModulePilotable | null {
  return MODULE_DE_L_ACTION[action] ?? null;
}

/**
 * Le module de la page affichée, par son URL sous `/:slug/` (même registre que
 * la navigation et la garde de l'API).
 */
export function moduleDeLaPage(cheminSousGalerie: string): Module | null {
  return moduleDeLaNavigation(cheminSousGalerie);
}

/**
 * La galerie que voit une PAGE : celle de la session, avec les modules en
 * lecture seule visibles (`modules`) et sans l'écriture de ces modules.
 *
 *  - Sur une page d'un module en lecture seule : plus aucune action
 *    d'écriture (créer, modifier, supprimer, envoyer, publier…).
 *  - Ailleurs (page du cœur qui montre un morceau de module) : seules les
 *    écritures PROPRES à un module en lecture seule tombent.
 *
 * `modulesLectureSeule` reste porté : `moduleEnLectureSeule` répond encore.
 * Sans module en lecture seule, la galerie est rendue telle quelle (même
 * objet), comme avant cette fonction.
 */
export function galerieDeLaPage(
  galerie: GalerieSession,
  moduleDeLaPageCourante: Module | null,
): GalerieSession {
  const lecture = modulesLectureSeuleDeLaSession(galerie);
  if (lecture.length === 0) return galerie;
  const pageEnLectureSeule =
    moduleDeLaPageCourante !== null &&
    (lecture as readonly Module[]).includes(moduleDeLaPageCourante);
  const actions = galerie.actions.filter((action) => {
    if (estActionDeLecture(action)) return true;
    if (pageEnLectureSeule) return false;
    const proprietaire = moduleDeLActionEcriture(action);
    return proprietaire === null || !lecture.includes(proprietaire);
  });
  const modules = [...new Set<Module>([...modulesDeLaSession(galerie), ...lecture])];
  return { ...galerie, modules, actions };
}

/**
 * Cette action d'écriture a-t-elle été retirée par la lecture seule ? C'est ce
 * qu'un bouton verrouillé a le droit de montrer : un rôle sans droit
 * d'écriture n'a pas de cadenas, il n'a simplement pas le bouton.
 */
export function actionVerrouillee(
  galerieReelle: GalerieSession,
  galeriePage: GalerieSession,
  action: Action,
): boolean {
  return galerieReelle.actions.includes(action) && !galeriePage.actions.includes(action);
}

/** Raison portée par la réponse : `details.raison` (repli `cause`, `detail`) ou le détail lui-même. */
function raisonDe(details: unknown): string | null {
  if (typeof details === 'string') return details;
  if (typeof details !== 'object' || details === null) return null;
  for (const cle of ['raison', 'cause', 'detail']) {
    const valeur = (details as Record<string, unknown>)[cle];
    if (typeof valeur === 'string') return valeur;
  }
  return null;
}

export const RAISON_LECTURE_SEULE = 'module_lecture_seule';

/** Le refus de l'API pour un module en lecture seule, lu sur son enveloppe. */
export function enveloppeEstRefusLectureSeule(
  status: number,
  code: string | undefined,
  details: unknown,
): boolean {
  return (
    status === 403 && code === 'module.indisponible' && raisonDe(details) === RAISON_LECTURE_SEULE
  );
}

/** `403 module.indisponible` de raison `module_lecture_seule`. */
export function estRefusLectureSeule(erreur: unknown): boolean {
  return (
    erreur instanceof ApiError &&
    enveloppeEstRefusLectureSeule(erreur.status, erreur.code, erreur.details)
  );
}

/**
 * Modules en lecture seule de la galerie affichée, posés par la coquille : de
 * quoi SIMULER le refus de l'API en mode démonstration (aucun serveur ne le
 * rendrait). Jamais lu hors démonstration.
 */
let modulesLectureSeuleCourants: readonly Module[] = [];
export function definirModulesLectureSeule(modules: readonly Module[]): void {
  modulesLectureSeuleCourants = modules;
}

/**
 * Le refus que l'API rendrait à cette écriture de démonstration, ou `null`.
 * `chemin` est celui de la requête (`/v1/g/<slug>/ventes/factures`).
 */
export function refusDemoLectureSeule(
  chemin: string,
  methode: string,
  message: string,
): ApiError | null {
  if (methode === 'GET' || modulesLectureSeuleCourants.length === 0) return null;
  const relatif = chemin.replace(/^\/v1\/g\/[^/]+/, '').split('?')[0] ?? '';
  const module = moduleDeLaRoute(relatif);
  if (module === null || !modulesLectureSeuleCourants.includes(module)) return null;
  return new ApiError(403, 'module.indisponible', message, { raison: RAISON_LECTURE_SEULE });
}
